export const DEFAULT_SERVER_URL = 'https://reporter.testdino.com';

import type { CompleteMetadata } from '../metadata/types.js';

export type {
  CompleteMetadata as RunMetadata,
  GitMetadata,
  CIMetadata,
  PRMetadata,
  SystemMetadata,
  PlaywrightMetadata,
  ShardMetadata,
  MetadataCollectionResult,
  MetadataCollectionSummary,
  MetadataCollector,
} from '../metadata/types.js';

export type { QuotaExhaustedError, QuotaExceededError, ServerError } from '../reporter/errors.js';
export type { SessionCreationQuotaResponse, NackMessage } from './errors.js';

export interface TestdinoConfig {
  token?: string;
  ciRunId?: string;
  // Manual-split group id — correlation key like ciRunId (CLI flag, testdino.config, reporter opts, or TESTDINO_SPLIT_ID env). The split position stays CLI-only.
  splitId?: string;
  // Swarm id — set only by `tdpw swarm --id` (CLI flag, threaded to the reporter via temp-config). Mutually exclusive with splitId.
  swarmId?: string;
  serverUrl?: string;
  debug?: boolean;
  artifacts?: boolean;
  tags?: string[];
}

export type TestEvent =
  | TestRunBeginEvent
  | TestBeginEvent
  | TestStepBeginEvent
  | TestStepEndEvent
  | TestEndEvent
  | TestRunEndEvent
  | TestRunErrorEvent
  | TestConsoleOutEvent
  | TestConsoleErrEvent
  | RunHeartbeatEvent;

export interface Annotation {
  type: string;
  description?: string;
}

export interface BaseEvent {
  type: string;
  timestamp: number;
  runId: string;
  sequence: number;
}

export interface TestRunBeginEvent extends BaseEvent {
  type: 'run:begin';
  projectId: string;
  metadata: CompleteMetadata;
  ciRunId?: string;
  shard?: {
    current: number;
    total: number;
  };
  splitId?: string;
  swarmId?: string;
  // machineId: the reporting machine under swarm; ingestion stamps it on every case for attribution.
  machineId?: string;
  split?: {
    current: number;
    total: number;
  };
  tags?: string[];
  // The run whose failed/flaky selection this run re-executes.
  rerunOf?: string;
}

export interface TestBeginEvent extends BaseEvent {
  type: 'test:begin';
  testId: string;
  title: string;
  titlePath: Array<string>;
  location: {
    file: string;
    line: number;
    column: number;
  };
  tags: Array<string>;
  expectedStatus: 'passed' | 'failed' | 'timedOut' | 'skipped' | 'interrupted';
  timeout: number;
  retries: number;
  annotations: Array<Annotation>;
  retry: number;
  workerIndex: number;
  parallelIndex: number;
  repeatEachIndex?: number;
  parentSuite: {
    title: string;
    type: 'root' | 'project' | 'file' | 'describe';
    location?: {
      file: string;
      line: number;
      column: number;
    };
  };
  startTime: number;
}

export interface TestStepBeginEvent extends BaseEvent {
  type: 'step:begin';
  testId: string;
  stepId: string;
  title: string;
  titlePath: Array<string>;
  category: string;
  location?: {
    file: string;
    line: number;
    column: number;
  };
  parentStep?: {
    title: string;
    category: string;
    location?: {
      file: string;
      line: number;
      column: number;
    };
  };
  startTime: number;
  retry: number;
  workerIndex: number;
  parallelIndex: number;
  annotations: Array<Annotation>;
}

export interface TestStepEndEvent extends BaseEvent {
  type: 'step:end';
  testId: string;
  stepId: string;
  title?: string;
  titlePath?: string[];
  duration: number;
  error?: {
    message: string;
    stack?: string;
    snippet?: string;
    value?: string;
    location?: {
      file: string;
      line: number;
      column: number;
    };
  };
  status: 'passed' | 'failed';
  childSteps: {
    count: number;
    steps: Array<{
      title: string;
      status: 'passed' | 'failed';
    }>;
  };
  workerIndex: number;
  parallelIndex: number;
  // step attachments never carry path — would leak local FS.
  attachments: Array<{
    name: string;
    contentType: string;
  }>;
  annotations?: Array<Annotation>;
  retry: number;
}

export interface TestEndEvent extends BaseEvent {
  type: 'test:end';
  testId: string;
  status: 'passed' | 'failed' | 'skipped' | 'timedOut' | 'interrupted';
  outcome: 'expected' | 'unexpected' | 'flaky' | 'skipped';
  duration: number;
  retry: number;
  workerIndex: number;
  parallelIndex: number;
  annotations: Array<Annotation>;
  errors: Array<{
    message: string;
    stack?: string;
    snippet?: string;
    value?: string;
    location?: {
      file: string;
      line: number;
      column: number;
    };
  }>;
  steps: {
    total: number;
    passed: number;
    failed: number;
  };
  // Three mutually-exclusive states: uploaded (blobKey) / failed (unavailable) / in-memory (excluded upstream).
  // path never emitted — non-dereferenceable + PII.
  attachments: Array<{
    name: string;
    contentType: string;
    blobKey?: string;
    unavailable?: boolean;
  }>;
  stdout?: string[];
  stderr?: string[];
}

export interface TestRunEndEvent extends BaseEvent {
  type: 'run:end';
  status: 'passed' | 'failed' | 'timedout' | 'interrupted';
  duration: number;
  startTime: number;
  shard?: {
    current: number;
    total: number;
  };
  splitId?: string;
  swarmId?: string;
  // machineId: the reporting machine under swarm; ingestion stamps it on every case for attribution.
  machineId?: string;
  split?: {
    current: number;
    total: number;
  };
  // Test Controls: what this process did with the quarantine list. Absent from
  // reporters that predate it; the server then stores no quarantine state.
  quarantine?: RunQuarantineBlock;
  // Tests this process brought to a final verdict, plus attempts still open at
  // interruption. The server compares its recorded results against this rather
  // than the run:begin skeleton, which cannot tell a short run from lost events.
  executedTests?: number;
  // Set when the run failed before its first test began (global setup or test
  // loading failed), so its run:begin declared no tests although some were meant
  // to run. Absent otherwise.
  notStarted?: true;
}

/** GET /api/v1/reporter/quarantine — Playwright test ids per Test Controls rule. */
export interface QuarantineList {
  quarantined: string[];
  knownErrors: { testIds: string[]; pattern: string }[];
  critical: string[];
  // One per rule, however many browser variants it expands to. Absent on
  // servers older than the count.
  rules?: { quarantined: number; knownErrors: number; critical: number };
  generatedAt?: string;
}

export interface RunQuarantineBlock {
  state: 'applied' | 'not_applied';
  reason?: 'unreachable' | 'timeout' | 'not_found';
  // Quarantined tests that ran here; a known-error test only if it passed or
  // its failure matched.
  testIds?: string[];
  // Critical tests that passed only on retry; the reporter failed the build.
  criticalFlakyTestIds?: string[];
  // The build result Test Controls ended with: its override, else Playwright's.
  // The server cannot rebuild it from counts (an error outside any test is
  // invisible there).
  build: 'passed' | 'failed';
}

// No sequence — not part of event ordering.
export interface RunHeartbeatEvent {
  type: 'run:heartbeat';
  runId: string;
  timestamp: number;
}

export interface TestRunErrorEvent extends BaseEvent {
  type: 'run:error';
  error: {
    message?: string;
    stack?: string;
    value?: string;
    snippet?: string;
    location?: {
      file: string;
      line: number;
      column: number;
    };
    // v1.49+
    cause?: {
      message?: string;
      stack?: string;
      value?: string;
      snippet?: string;
      location?: {
        file: string;
        line: number;
        column: number;
      };
    };
  };
}

export interface TestConsoleOutEvent extends BaseEvent {
  type: 'console:out';
  text: string; // Chunk, UTF-8, max 10KB.
  testId?: string;
  retry?: number;
  truncated?: boolean;
}

export interface TestConsoleErrEvent extends BaseEvent {
  type: 'console:err';
  text: string; // Chunk, UTF-8, max 10KB.
  testId?: string;
  retry?: number;
  truncated?: boolean;
}
